import React, { useEffect, useState } from "react";
import API from "../api/api";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [currentAgent, setCurrentAgent] = useState({});
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    role_id: "",
  });

  const fetchAgents = async () => {
    try {
      const response = await API.get("/Agents");
      setAgents(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch agents");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdd = async () => {
    try {
      await API.post("/Agents", formData);
      fetchAgents();
      setShowAdd(false);
      setFormData({ username: "", email: "", role_id: "" });
    } catch (err) {
      alert("Failed to add agent: " + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = async () => {
    try {
      await API.put(`/Agents/${currentAgent.id}`, formData);
      fetchAgents();
      setShowEdit(false);
    } catch (err) {
      alert("Failed to update agent: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/Agents/${currentAgent.id}`);
      fetchAgents();
      setShowDelete(false);
    } catch (err) {
      alert("Failed to delete agent: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Agents"
        description="Manage agent accounts and role assignments."
        actions={
          <Button variant="primary" onClick={() => setShowAdd(true)}>
            <FaPlus /> Add Agent
          </Button>
        }
      />

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="vp-loading">Loading agents…</div>
      ) : agents.length === 0 ? (
        <EmptyState
          title="No agents yet"
          description="Add an agent to start assigning bookings and access."
          action={
            <Button variant="primary" onClick={() => setShowAdd(true)}>
              <FaPlus /> Add Agent
            </Button>
          }
        />
      ) : (
        <div className="vp-table-wrap table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Agent Name</th>
                <th>Email</th>
                <th>Role ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.id}>
                  <td>{agent.id}</td>
                  <td>{agent.username}</td>
                  <td>{agent.email}</td>
                  <td>{agent.role_id}</td>
                  <td>
                    <div className="vp-table-actions">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => {
                          setCurrentAgent(agent);
                          setFormData({
                            username: agent.username,
                            email: agent.email,
                            role_id: agent.role_id,
                          });
                          setShowEdit(true);
                        }}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setCurrentAgent(agent);
                          setShowDelete(true);
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Agent Name</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role ID</Form.Label>
              <Form.Control
                type="number"
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Agent Name</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role ID</Form.Label>
              <Form.Control
                type="number"
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{currentAgent.username}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};

export default Agents;
